import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Logo } from '../components/Logo';
import { Input } from '../components/ui/input';
import { Leaf, Shield, Mail, Loader2, CheckCircle2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotAdminSecretPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await axios.post(`${API}/admin/forgot-secret`, {
        admin_email: email
      });
      setEmailSent(true);
      toast.success('Reset instructions sent if email is registered');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <header className="border-b border-border bg-white/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size="default" linkTo="/" />
        </div>
      </header>

      <main className="max-w-md mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-sm border border-border p-8"
        >
          {emailSent ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-semibold text-[#1A1A1A] mb-2">Check Your Email</h1>
              <p className="text-muted-foreground mb-6">
                If {email} is registered as the admin email, you'll receive a link to reset your admin secret.
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                The link will expire in 1 hour.
              </p>
              <Link to="/admin">
                <Button variant="outline" className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Admin Login
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-amber-600" />
                </div>
                <h1 className="text-2xl font-semibold text-[#1A1A1A]">Forgot Admin Secret?</h1>
                <p className="text-muted-foreground mt-2">
                  Enter the admin email to receive a reset link
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Admin Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="admin@example.com"
                      className="pl-10"
                      required
                      data-testid="admin-email-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    This should be the email address configured as the admin recovery email.
                  </p>
                </div>

                <Button 
                  type="submit" 
                  disabled={isLoading} 
                  className="w-full btn-primary"
                  data-testid="send-reset-btn"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <Link
                  to="/admin"
                  className="text-sm text-muted-foreground hover:text-primary"
                >
                  <ArrowLeft className="w-4 h-4 inline mr-1" />
                  Back to Admin Login
                </Link>
              </div>
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
}
